import React from 'react';
import { motion } from 'framer-motion';

const steps = [
  {
    number: '01',
    title: 'Discover',
    description: 'We learn how your business actually runs — the workflows, the bottlenecks, and the work software should be doing for you.',
  },
  {
    number: '02',
    title: 'Design',
    description: 'A clear plan and a real, interactive design. You see exactly what you are getting before a line of production code ships.',
  },
  {
    number: '03',
    title: 'Build',
    description: 'We ship in milestones on a modern stack, so you see working software early and often — with billing tied to delivery, not hours.',
  },
  {
    number: '04',
    title: 'Grow',
    description: 'Launch is the start. We support, refine, and add the features your business needs as it scales — software that grows with you.',
  },
];

const Process: React.FC = () => {
  return (
    <section className="py-20 bg-black relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          className="text-center mb-14 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gold-400 mb-4">How We Work</h2>
          <p className="text-gray-400">
            A straightforward, milestone-driven process — built for owners who want clarity, not jargon.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, i) => (
            <motion.div
              key={step.number}
              className="relative bg-gray-900/60 border border-gold-800/30 rounded-3xl p-7 backdrop-blur-sm hover:border-gold-600/50 transition-all duration-300"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              whileHover={{ y: -6 }}
            >
              <span className="block text-5xl font-bold text-gold-400/25 mb-3">{step.number}</span>
              <h3 className="text-lg font-bold text-gold-400 mb-2">{step.title}</h3>
              <p className="text-gray-300 text-sm leading-relaxed">{step.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
      <motion.div
        className="absolute top-0 right-0 w-96 h-96 bg-gold-500/5 rounded-full blur-3xl"
        animate={{ x: [0, 30, 0], y: [0, 30, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
      />
    </section>
  );
};

export default Process;
